import { useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { DEFAULT_TIMEOUT, exportDataAsCsv, fetchHistoricalData, fetchHistoricalFaults } from '../utils/api'
import { validateTimeRange } from '../utils/dateUtils'
import { deviceNames } from '../utils/deviceDefinitions'
import { measurementDefinitions } from '../utils/measurementDefinitions'

const DOWNSAMPLE_THRESHOLD = 1000
const TARGET_SAMPLE_SIZE = 500

// Downsample as a pure function
const downsample = (data, threshold) => {
  if (data.length <= threshold) return data
  const sampled = [data[0]]
  const bucketSize = data.length / threshold
  for (let i = 1; i < threshold - 1; i++) {
    const bucketStart = Math.floor((i - 1) * bucketSize) + 1
    const bucketEnd = Math.floor((i + 1) * bucketSize) + 1
    const bucketCenter = Math.floor(i * bucketSize) + 1
    let maxArea = -1, maxAreaIndex = bucketStart
    const a = sampled[sampled.length - 1]
    for (let j = bucketStart; j < bucketCenter; j++) {
      const pointC = data[bucketEnd >= data.length ? data.length - 1 : bucketEnd]
      const pointB = data[j]
      const area = Math.abs((a.x - pointC.x) * (pointB.y - a.y) - (a.x - pointB.x) * (pointC.y - a.y)) * 0.5
      if (area > maxArea) { maxArea = area; maxAreaIndex = j }
    }
    sampled.push(data[maxAreaIndex])
  }
  sampled.push(data[data.length - 1])
  return sampled
}

const calcStats = dataset => {
  if (!dataset?.length) return {}
  const statsMap = {}
  dataset.forEach(({ device, measurement, _value }) => {
    const key = `${device}-${measurement}`
    if (!statsMap[key]) statsMap[key] = { min: _value, max: _value, sum: _value, count: 1 }
    else {
      const stat = statsMap[key]
      stat.min = Math.min(stat.min, _value)
      stat.max = Math.max(stat.max, _value)
      stat.sum += _value
      stat.count++
    }
  })
  return Object.fromEntries(Object.entries(statsMap).map(([key, stat]) => [key, {
    min: +stat.min.toFixed(3),
    max: +stat.max.toFixed(3),
    avg: +(stat.sum / stat.count).toFixed(3),
    range: +(stat.max - stat.min).toFixed(3),
  }]))
}

// Query function for historical data
const fetchHistoricalDataQueryFn = async ({ queryKey, signal }) => {
  const [_key, devices, measurements, timeRange] = queryKey
  let validatedTimeRange
  try {
    validatedTimeRange = validateTimeRange(timeRange)
  } catch (err) {
    throw new RangeError(`Invalid time range: ${err.message}`)
  }

  if (validatedTimeRange.duration === 'allTime' && !validatedTimeRange.startTime && !validatedTimeRange.endTime) {
    return []
  }

  const { startTime, endTime } = validatedTimeRange
  const combinations = devices.flatMap(device => measurements.map(measurement => ({ device, measurement })))

  if (!combinations.length) {
    return []
  }

  const batchSize = 4
  const allResponsesData = []
  
  let overallTimeoutId
  const overallTimeoutPromise = new Promise((_, reject) => {
    overallTimeoutId = setTimeout(() => reject(new Error('Overall data loading timed out.')), DEFAULT_TIMEOUT + 5000)
  })

  // Listen for TanStack Query's abort signal to clear our custom timeout
  if (signal) {
    signal.addEventListener('abort', () => {
      if (overallTimeoutId) {
        clearTimeout(overallTimeoutId)
      }
    })
  }

  try {
    for (let i = 0; i < combinations.length; i += batchSize) {
      // Check signal before each batch
      if (signal.aborted) throw new Error('Query aborted by signal')

      const batch = combinations.slice(i, i + batchSize)
      const batchPromises = batch.map(({ device, measurement }) =>
        fetchHistoricalData(device, measurement, startTime, endTime, signal),
      )
      
      const batchResponses = await Promise.race([Promise.all(batchPromises), overallTimeoutPromise])

      batchResponses.forEach(response => {
        if (response?.data && !response.data.empty && Array.isArray(response.data)) {
          allResponsesData.push(...response.data)
        }
      })
    }
    clearTimeout(overallTimeoutId) // Clear timeout if all batches succeed
    return allResponsesData
  } catch (error) {
    clearTimeout(overallTimeoutId) // Ensure timeout is cleared on any error
    if (signal.aborted && error.message !== 'Query aborted by signal') {
      // If the signal was aborted but the error is different, it might be from overallTimeoutPromise
      // or a non-abort error from fetchHistoricalData. We prioritize the signal's abort.
      throw new Error('Query aborted by signal')
    }
    throw error // Re-throw the caught error (either from timeout or batchPromises)
  }
}

// Query function for faults data
const fetchFaultsQueryFn = async ({ queryKey, signal }) => {
  const [_key, rawTimeRange] = queryKey
  let validatedTimeRange
  try {
    validatedTimeRange = validateTimeRange(rawTimeRange)
  } catch {
    return [] // Return empty on validation error, or handle error appropriately
  }

  if (!validatedTimeRange || !validatedTimeRange.startTime || !validatedTimeRange.endTime ||
      !(validatedTimeRange.startTime instanceof Date) || !(validatedTimeRange.endTime instanceof Date)) {
    return []
  }
  const { data: faultData } = await fetchHistoricalFaults(validatedTimeRange.startTime, validatedTimeRange.endTime, signal)
  return faultData?.empty ? [] : (Array.isArray(faultData) ? faultData : [])
}

export function useHistoricalData(selectedDevices, selectedMeasurements, timeRange, showFaults, showStats, showConfigChanges, configSnapshots) {

  const historicalDataQuery = useQuery({
    queryKey: ['historicalData', selectedDevices, selectedMeasurements, timeRange],
    queryFn: fetchHistoricalDataQueryFn,
    enabled: selectedDevices.length > 0 && selectedMeasurements.length > 0 && !!timeRange,
    staleTime: 1000 * 60 * 2, // 2 minutes
  })

  const faultsQuery = useQuery({
    queryKey: ['faults', timeRange],
    queryFn: fetchFaultsQueryFn,
    enabled: showFaults && !!timeRange,
    staleTime: 1000 * 60 * 2, // 2 minutes
  })

  // Wrap in useMemo to stabilize dependencies for other useMemo/useEffect hooks
  const data = useMemo(() => historicalDataQuery.data || [], [historicalDataQuery.data])
  const faults = useMemo(() => faultsQuery.data || [], [faultsQuery.data])

  const loading = historicalDataQuery.isLoading
  const faultsLoading = faultsQuery.isLoading
  const error = historicalDataQuery.error?.message || faultsQuery.error?.message || null
 
  const statsData = useMemo(() => {
    if (showStats && data.length > 0) {
      return calcStats(data)
    }
    return {}
  }, [data, showStats])

  const loadData = useCallback(() => {
    historicalDataQuery.refetch()
    // Only refetch faults if they are supposed to be shown
    if (showFaults) {
      faultsQuery.refetch()
    }
  }, [historicalDataQuery, faultsQuery, showFaults])

  const handleExportData = useCallback(async () => {
    if (!selectedDevices.length || !selectedMeasurements.length) {
      console.error('Please select at least one device and measurement to export.')
      return
    }
    const exportAbortController = new AbortController()
    const { signal } = exportAbortController
    const timeoutId = setTimeout(() => {
      exportAbortController.abort()
      console.error('Export operation timed out.')
    }, 60000)
    try {
      const validatedTimeRange = validateTimeRange(timeRange)
      if (!validatedTimeRange.startTime || !validatedTimeRange.endTime) {
        console.error('Invalid time range for export')
        return
      }
      const response = await exportDataAsCsv(selectedDevices, selectedMeasurements, validatedTimeRange.startTime, validatedTimeRange.endTime, signal)
      if (response?.data instanceof Blob && response.data.size > 0) {
        const blob = response.data
        const startTimeStr = validatedTimeRange.startTime.toISOString()
        const endTimeStr = validatedTimeRange.endTime.toISOString()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `historical-data-${startTimeStr}_to_${endTimeStr}.csv`
        a.style.display = 'none'
        document.body.appendChild(a)
        a.click()
        URL.revokeObjectURL(url)
        document.body.removeChild(a)
      } else if (response?.data instanceof Blob && response.data.size === 0) {
        console.error('No data found for the selected criteria to export.')
      } else {
        console.error('Received an invalid response during export.')
      }
    } catch (err) {
      console.error(`Export failed: ${err.message || 'Unknown export error'}`)
    } finally {
      clearTimeout(timeoutId)
    }
  }, [selectedDevices, selectedMeasurements, timeRange])

  const series = useMemo(() => {
    if (!data.length) return []
    const dataMap = {}
    data.forEach(d => {
      const key = `${d.device}_${d.measurement}`;
      (dataMap[key] ||= []).push({ x: new Date(d._time).getTime(), y: d._value })
    })
    Object.values(dataMap).forEach(points => {
      points.sort((a, b) => a.x - b.x)
      if (points.length > DOWNSAMPLE_THRESHOLD) {
        const sampledPoints = downsample(points, TARGET_SAMPLE_SIZE)
        points.length = 0; points.push(...sampledPoints)
      }
    })

    const validSelectedMeasurements = selectedMeasurements.filter(m => m != null)

    return validSelectedMeasurements.flatMap(measurement =>
      selectedDevices.map(device => {
        const key = `${device}_${measurement}`
        const points = dataMap[key] || []
        const deviceFriendlyName = deviceNames[device] || device
        const measDef = measurementDefinitions[measurement]
        const measurementFriendlyName = measDef?.friendlyName || measurement
        return {
          name: `${deviceFriendlyName} - ${measurementFriendlyName}`,
          data: points,
          deviceId: device,
          measurementId: measurement,
        }
      }),
    ).filter(s => s.data.length > 0) // Ensure series with data are returned
  }, [selectedDevices, selectedMeasurements, data])

  const annotations = useMemo(() => {
    const xaxisAnnotations = []

    if (showFaults && faults && faults.length > 0) {
      faults.forEach((fault, _index) => {
        const faultTime = new Date(fault.time).getTime()
        if (!isNaN(faultTime)) {
          xaxisAnnotations.push({
            x: faultTime,
            borderColor: '#FF4560',
            label: {
              borderColor: '#FF4560',
              style: { color: '#fff', background: '#FF4560' },
              text: `${fault.device} - ${fault.measurement} (Fault)`,
            },
          })
        }
      })
    }

    if (showConfigChanges && configSnapshots && configSnapshots.length > 0) {
      configSnapshots.forEach((snapshot, _index) => {
        const snapshotTime = new Date(snapshot.time).getTime()
        if (!isNaN(snapshotTime)) {
          xaxisAnnotations.push({
            x: snapshotTime,
            borderColor: '#008FFB',
            label: {
              borderColor: '#008FFB',
              style: { color: '#fff', background: '#008FFB' },
              text: 'Config Change',
            },
          })
        }
      })
    }

    if (xaxisAnnotations.length === 0) {
      return {}
    }

    return {
      xaxis: xaxisAnnotations.sort((a, b) => a.x - b.x),
    }
  }, [showFaults, faults, showConfigChanges, configSnapshots])

  return {
    data,
    faults,
    loading,
    faultsLoading,
    error,
    statsData,
    series,
    annotations,
    loadData, // This now calls refetch
    handleExportData,
  }
}